import type React from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronRight, Calendar, Hash } from 'lucide-react';
import { Badge } from '@/components/ui';
import { cn } from '@/lib/utils';
import type { Order } from '@/types/models';
import { OrderStatus } from '@/types/enums';

interface OrderCardProps {
  order: Order;
  className?: string;
}

const OrderCard: React.FC<OrderCardProps> = ({ order, className }) => {
  const navigate = useNavigate();
  const isHighlighted = order.status === OrderStatus.ARRIVED;

  return (
    <button
      onClick={() => navigate(`/my-orders/${order.id}`)}
      className={cn(
        'w-full text-left bg-white rounded-2xl border transition-all duration-200',
        'hover:shadow-md hover:-translate-y-0.5 active:scale-[0.995]',
        isHighlighted
          ? 'border-blue-200 ring-1 ring-blue-100'
          : 'border-gray-100 hover:border-gray-200',
        className,
      )}
    >
      <div className="p-5">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 mb-2">
              <Hash className="w-3.5 h-3.5 text-gray-400 shrink-0" />
              <span className="text-base font-semibold text-gray-900 truncate">
                {order.order_number}
              </span>
            </div>

            <Badge.Status status={order.status} />

            <div className="flex items-center gap-1.5 mt-3 text-xs text-gray-400">
              <Calendar className="w-3 h-3" />
              <span>{new Date(order.created_at).toLocaleDateString('ru-RU')}</span>
            </div>

            {order.shelf_location && (
              <p className="mt-1.5 text-xs text-gray-500">
                Полка: <span className="font-medium text-gray-700">{order.shelf_location}</span>
              </p>
            )}
          </div>

          <ChevronRight className="w-5 h-5 text-gray-300 shrink-0 mt-1" />
        </div>
      </div>
    </button>
  );
};

export default OrderCard;
